'use client'

import useSWR from 'swr'
import { SearchNormal1 } from 'iconsax-react'
import { FC, useEffect, useState } from 'react'
import { useParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import ServicesCard from '../../Services/ServicesCard'
import { ApiRoutes } from '@/api/routes'
import { fetcher } from '@/api/functions'
import { Leaf } from '@/components/icons'
import { IServicesFullData, IServicesTransformedData } from '@/types'

const ServicesPage: FC<{ category: string }> = ({ category }) => {
  const t = useTranslations('Search')
  const [services, setServices] = useState<IServicesTransformedData[] | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const params = useParams()

  const { data } = useSWR<IServicesFullData[]>(
    ApiRoutes.services.services + `?lang=${params.locale}` + category,
    fetcher
  )

  useEffect(() => {
    if (data) {
      const transformedServices: IServicesTransformedData[] = data.reduce(
        (acc: IServicesTransformedData[], currentItem: IServicesFullData) => {
          const existingCategory = acc.find((item) => item.category.id === currentItem.category.id)

          if (existingCategory) {
            existingCategory.data.push(currentItem)
          } else {
            acc.push({
              category: { id: currentItem.category.id, name: currentItem.category.name },
              category_parent: {
                id: currentItem.category_parent?.id,
                name: currentItem.category_parent?.name,
              },
              data: [currentItem],
            })
          }

          return acc
        },
        []
      )
      if (!searchQuery) {
        setServices(transformedServices)
      } else {
        const filteredServices = transformedServices
          .map(({ category, category_parent, data }) => ({
            category,
            category_parent,
            data: data.filter((service) =>
              service.name.toLowerCase().includes(searchQuery.toLowerCase())
            ),
          }))
          .filter(({ data }) => data.length >= 1)
        setServices(filteredServices)
      }
    }
  }, [data, searchQuery])

  const inputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  return (
    <div>
      <div className="relative mb-6">
        <SearchNormal1 size={20} className="absolute left-5 top-4 text-secondary" />
        <input
          type="text"
          placeholder={t('search')}
          className=" w-full rounded-lg border border-secondary-light bg-white px-[48px] py-[13px] outline-none focus:border-secondary"
          value={searchQuery}
          onChange={inputHandler}
        />
      </div>
      <div className="flex flex-col gap-6">
        {services !== null ? (
          services.length >= 1 ? (
            services.map(({ category, data }) => (
              <div
                className="flex flex-col gap-2"
                key={'services-item-' + category.name + category.id}
                id={category.name.replaceAll(' ', '-').toLowerCase()}
              >
                <h5>{category.name}</h5>
                {data.map((val, i) => (
                  <ServicesCard key={'services-item-' + val.slug + i} {...val} />
                ))}
              </div>
            ))
          ) : (
            t('notFound')
          )
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex w-full flex-row items-center gap-4 rounded-[12px] border border-secondary-light bg-white px-[20px] py-[16px] outline-none duration-200 hover:border-primary focus:border-secondary">
              <Leaf />
              <span className="h-[15px] w-[80%] animate-pulse rounded-full bg-secondary-light font-medium" />
            </div>
            <div className="flex w-full flex-row items-center gap-4 rounded-[12px] border border-secondary-light bg-white px-[20px] py-[16px] outline-none duration-200 hover:border-primary focus:border-secondary">
              <Leaf />
              <span className="h-[15px] w-[80%] animate-pulse rounded-full bg-secondary-light font-medium" />
            </div>
            <div className="flex w-full flex-row items-center gap-4 rounded-[12px] border border-secondary-light bg-white px-[20px] py-[16px] outline-none duration-200 hover:border-primary focus:border-secondary">
              <Leaf />
              <span className="h-[15px] w-[80%] animate-pulse rounded-full bg-secondary-light font-medium" />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ServicesPage
